import React, { useState, createContext, useContext } from 'react';
import { Camera, Download, Trash2, X, Loader2, Images, Check, Square, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';

interface CapturedScreenshot {
  id: string;
  name: string;
  dataUrl: string;
  timestamp: number;
}

interface ScreenshotSessionContextType {
  isActive: boolean;
  startSession: () => void;
  endSession: () => void;
  screenshots: CapturedScreenshot[];
}

const ScreenshotSessionContext = createContext<ScreenshotSessionContextType | null>(null);

export function useScreenshotSession() {
  return useContext(ScreenshotSessionContext);
}

export function ScreenshotSessionProvider({ children }: { children: React.ReactNode }) {
  const [isActive, setIsActive] = useState(false);
  const [screenshots, setScreenshots] = useState<CapturedScreenshot[]>([]);

  // Where to go back to while doing a screenshot session.
  // Using history + popstate keeps this as SPA navigation (no full page reload).
  const basePath = '/admin/video-assets';

  const startSession = () => {
    setIsActive(true);
    setScreenshots([]);
    toast.success('Screenshot-Session gestartet! Navigiere durch die App und klicke auf den Kamera-Button.');
  };

  const endSession = () => {
    setIsActive(false);
  };

  return (
    <ScreenshotSessionContext.Provider value={{ isActive, startSession, endSession, screenshots }}>
      {children}
      {isActive && (
        <FloatingScreenshotTool
          basePath={basePath}
          screenshots={screenshots}
          setScreenshots={setScreenshots}
          onEnd={endSession}
        />
      )}
    </ScreenshotSessionContext.Provider>
  );
}

function FloatingScreenshotTool({ 
  basePath,
  screenshots, 
  setScreenshots, 
  onEnd 
}: { 
  basePath: string;
  screenshots: CapturedScreenshot[];
  setScreenshots: React.Dispatch<React.SetStateAction<CapturedScreenshot[]>>;
  onEnd: () => void;
}) {
  const [isCapturing, setIsCapturing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [lastCapture, setLastCapture] = useState<string | null>(null);

  const navigateSpa = (path: string) => {
    if (!path || window.location.pathname === path) return;
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const captureScreenshot = async () => {
    setIsCapturing(true);
    try {
      // Find the main content - try different selectors
      const mainElement = document.querySelector('main') || 
                         document.querySelector('[class*="max-w"]') ||
                         document.body;
      
      const canvas = await html2canvas(mainElement as HTMLElement, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        scale: 2,
        logging: false,
        // Ignore the floating tool itself
        ignoreElements: (element) => {
          return element.id === 'screenshot-floating-tool';
        },
      });
      
      const dataUrl = canvas.toDataURL('image/png');
      const pathname = window.location.pathname;
      const name = pathname === '/' ? 'landing' : pathname.replace(/\//g, '-').slice(1);
      const index = screenshots.length + 1;
      
      const newScreenshot: CapturedScreenshot = {
        id: Date.now().toString(),
        name: `${String(index).padStart(2, '0')}-${name}`,
        dataUrl,
        timestamp: Date.now(),
      };
      
      setScreenshots(prev => [...prev, newScreenshot]);
      setLastCapture(dataUrl);
      toast.success(`Screenshot ${index} gespeichert: ${name}`);
      
      // Clear preview after 2 seconds
      setTimeout(() => setLastCapture(null), 2000);
    } catch (error) {
      console.error('Screenshot failed:', error);
      toast.error('Screenshot fehlgeschlagen');
    } finally {
      setIsCapturing(false);
    }
  };

  const downloadAll = async () => {
    if (screenshots.length === 0) {
      toast.error('Keine Screenshots vorhanden');
      return;
    }

    setIsDownloading(true);
    try {
      const zip = new JSZip();
      
      for (const screenshot of screenshots) {
        const response = await fetch(screenshot.dataUrl);
        const blob = await response.blob();
        zip.file(`${screenshot.name}.png`, blob);
      }
      
      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = `germanpath-screenshots-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`${screenshots.length} Screenshots als ZIP heruntergeladen!`);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download fehlgeschlagen');
    } finally {
      setIsDownloading(false);
    }
  };

  const clearAll = () => {
    setScreenshots([]);
    toast.success('Alle Screenshots gelöscht');
  };

  return (
    <div 
      id="screenshot-floating-tool"
      className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 items-end"
    >
      {/* Last capture preview */}
      {lastCapture && (
        <div className="bg-card border border-green-500 rounded-lg shadow-lg p-2 animate-in slide-in-from-right-2">
          <img src={lastCapture} alt="Last capture" className="w-32 h-20 object-cover rounded" />
          <Check className="absolute top-1 right-1 h-5 w-5 text-green-500 bg-card rounded-full p-0.5" />
        </div>
      )}

      {/* Expanded Panel */}
      {isExpanded && (
        <div className="bg-card border border-border rounded-lg shadow-lg p-4 w-80 animate-in slide-in-from-right-2">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <Images className="h-4 w-4 text-primary" />
              Screenshot-Session
            </h3>
            <div className="flex items-center gap-1">
              <Badge variant="secondary">{screenshots.length}</Badge>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setIsExpanded(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Thumbnail Grid */}
          {screenshots.length > 0 && (
            <div className="grid grid-cols-4 gap-1 mb-3 max-h-24 overflow-y-auto">
              {screenshots.map((s, idx) => (
                <div key={s.id} className="relative">
                  <img 
                    src={s.dataUrl} 
                    alt={s.name} 
                    className="w-full h-12 object-cover rounded border"
                  />
                  <span className="absolute bottom-0 right-0 bg-black/70 text-white text-[10px] px-1 rounded-tl">
                    {idx + 1}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="space-y-2">
            <Button
              onClick={() => navigateSpa(basePath)}
              variant="outline"
              size="sm"
              className="w-full gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Zurück zu Video-Assets
            </Button>

            <Button 
              onClick={captureScreenshot} 
              disabled={isCapturing}
              className="w-full gap-2"
              size="sm"
            >
              {isCapturing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Erfasse...
                </>
              ) : (
                <>
                  <Camera className="h-4 w-4" />
                  Screenshot aufnehmen (#{screenshots.length + 1})
                </>
              )}
            </Button>
            
            {screenshots.length > 0 && (
              <div className="flex gap-2">
                <Button 
                  onClick={downloadAll}
                  disabled={isDownloading}
                  variant="default"
                  size="sm"
                  className="flex-1 gap-2"
                >
                  {isDownloading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  ZIP Download
                </Button>
                <Button 
                  onClick={clearAll}
                  variant="outline"
                  size="sm"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}

            <Button 
              onClick={onEnd}
              variant="outline"
              size="sm"
              className="w-full gap-2"
            >
              <Square className="h-4 w-4" />
              Session beenden
            </Button>
          </div>

          <p className="text-xs text-muted-foreground mt-3">
            Aktuelle Seite: <code className="bg-muted px-1 rounded">{window.location.pathname}</code>
          </p>
        </div>
      )}

      {/* Collapsed: Just the capture button */}
      {!isExpanded && (
        <div className="flex gap-2">
          <Button
            onClick={() => navigateSpa(basePath)}
            size="icon"
            variant="outline"
            className="rounded-full h-12 w-12 shadow-lg"
            aria-label="Zurück zu Video-Assets"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Button
            onClick={() => setIsExpanded(true)}
            size="icon"
            variant="outline"
            className="rounded-full h-12 w-12 shadow-lg"
          >
            <Images className="h-5 w-5" />
            {screenshots.length > 0 && (
              <Badge 
                variant="default" 
                className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-0 text-xs"
              >
                {screenshots.length}
              </Badge>
            )}
          </Button>
          <Button
            onClick={captureScreenshot}
            disabled={isCapturing}
            size="icon"
            className="rounded-full h-12 w-12 shadow-lg"
          >
            {isCapturing ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Camera className="h-5 w-5" />
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
